import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  UserCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  UsersIcon,
  CheckBadgeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { adminManagementAPI } from '../services/api';
import { User, PaginationInfo } from '../types';
import { toast } from 'react-toastify';
import PasswordInput from '../components/PasswordInput';
import { PasswordValidationResult } from '../utils/passwordValidation';
import { formatDate } from '../utils/dateUtils';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showViewUserModal, setShowViewUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'candidate' as 'candidate' | 'employer' | 'admin',
    firstName: '',
    lastName: '',
    companyName: ''
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });
  // Enhanced state for performance monitoring
  const [performance, setPerformance] = useState<{
    queriedUsers: number;
    filteredUsers: number;
    estimatedTotal: number;
    lastLoadTime?: number;
  }>({
    queriedUsers: 0,
    filteredUsers: 0,
    estimatedTotal: 0
  });
  const [userActionHistory, setUserActionHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [loadingActions, setLoadingActions] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadUsers();
  }, [pagination.currentPage, selectedRole, statusFilter, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const startTime = Date.now();
      const response = await adminManagementAPI.getUsers({
        page: pagination.currentPage,
        limit: 10,
        role: selectedRole || undefined,
        search: searchTerm || undefined,
        status: statusFilter || undefined
      });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
      
      // Update performance state
      setPerformance(prev => ({
        ...prev,
        queriedUsers: response.data.users.length,
        filteredUsers: response.data.users.filter((user: User) => {
          // Apply the same filtering logic used in the UI
          const matchesRole = selectedRole ? user.activeRoles.includes(selectedRole) : true;
          const matchesStatus = statusFilter ? 
            (statusFilter === 'active' ? user.activeRoles.length > 0 : user.activeRoles.length === 0) : true;
          const matchesSearch = searchTerm ? 
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) 
            : true;
          return matchesRole && matchesStatus && matchesSearch;
        }).length,
        estimatedTotal: response.data.pagination.totalItems,
        lastLoadTime: Date.now() - startTime
      }));
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      setLoadingActions(prev => ({ ...prev, [userId]: true }));
      await adminManagementAPI.toggleUserStatus(userId);
      
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === userId ? { 
          ...user, 
          // Compute new status - if user was active, they become inactive and vice versa
          activeRoles: currentStatus ? [] : user.activeRoles || [user.role],
          roles: user.roles?.map(role => ({
            ...role,
            isActive: !currentStatus
          })) || []
        } : user
      ));
      
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status');
    } finally {
      setLoadingActions(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleToggleSpecificRole = async (userId: string, roleName: string, currentRoleStatus: boolean) => {
    try {
      setLoadingActions(prev => ({ ...prev, [`${userId}-${roleName}`]: true }));
      
      // console.log('🔄 Toggling role:', { userId, roleName, currentRoleStatus });
      
      const response = await adminManagementAPI.toggleUserRoleStatus(userId, roleName);
      
      // console.log('✅ API Response:', response.data);
      
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === userId ? { 
          ...user, 
          roles: user.roles?.map(role => 
            role.role === roleName ? { ...role, isActive: !currentRoleStatus } : role
          ) || [],
          // Fix: Create the updated roles first, then filter and map
          activeRoles: (() => {
            const updatedRoles = user.roles?.map(role => 
              role.role === roleName ? { ...role, isActive: !currentRoleStatus } : role
            ) || [];
            const newActiveRoles = updatedRoles.filter(role => role.isActive).map(role => role.role);
            // console.log('🔄 Updated activeRoles:', newActiveRoles);
            return newActiveRoles;
          })()
        } : user
      ));
      
      toast.success(`${roleName} role ${!currentRoleStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling role status:', error);
      toast.error(`Failed to toggle ${roleName} role`);
    } finally {
      setLoadingActions(prev => ({ ...prev, [`${userId}-${roleName}`]: false }));
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadUsers();
  };

  // Add a function to handle individual filter clearing
  const handleClearIndividualFilter = (filterType: 'search' | 'role' | 'status') => {
    switch (filterType) {
      case 'search':
        setSearchTerm('');
        break;
      case 'role':
        setSelectedRole('');
        break;
      case 'status':
        setStatusFilter('');
        break;
    }
    // Reset to first page when clearing any filter
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = async () => {
    // Clear all filter states
    setSearchTerm('');
    setSelectedRole('');
    setStatusFilter('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Directly call the API with cleared filters instead of relying on state updates
    try {
      setLoading(true);
      const response = await adminManagementAPI.getUsers({
        page: 1,
        limit: 10,
        // Explicitly pass undefined/empty values to ensure filters are cleared
        role: undefined,
        search: undefined,
        status: undefined
      });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddUserModal = () => {
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setPasswordValidation(null);
    setNewUser({
      email: '',
      password: '',
      role: 'candidate',
      firstName: '',
      lastName: '',
      companyName: ''
    });
  };

  const handlePasswordChange = (password: string) => {
    setNewUser({ ...newUser, password });
  };

  const handlePasswordValidation = (result: PasswordValidationResult) => {
    setPasswordValidation(result);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passwordValidation?.isValid) {
      toast.error('Please ensure the password meets all security requirements');
      return;
    }
    
    setAddUserLoading(true);
    try {
      const response = await adminManagementAPI.createUser(newUser);
      setUsers([response.data.user, ...users]);
      setPagination(prev => ({ ...prev, totalItems: prev.totalItems + 1 }));
      toast.success('User created successfully');
      handleCloseAddUserModal();
      loadUsers(); // Refresh the list
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create user';
      toast.error(message);
    } finally {
      setAddUserLoading(false);
    }
  };

  const loadUserActionHistory = async (userId: string) => {
    setLoadingHistory(true);
    try {
      // Use the new comprehensive user activity endpoint
      const response = await adminManagementAPI.getUserActivity(userId, { limit: 50 });
      
      // The response now includes both actions BY the user and actions ON the user
      const logs = response.data.logs.map((log: any) => ({
        ...log,
        type: log.activityType === 'authentication' ? 'auth' : 'database',
        source: log.source || 'Unknown'
      }));

      setUserActionHistory(logs);
      
      // Log the summary for debugging
      // console.log('📊 User Activity Summary:', response.data.summary);
      
    } catch (error) {
      console.error('Error loading user history:', error);
      toast.error('Failed to load user action history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenViewUserModal = (user: User) => {
    setSelectedUser(user);
    setShowViewUserModal(true);
    // Load user action history when opening modal
    loadUserActionHistory(user.id);
  };

  const handleCloseViewUserModal = () => {
    setShowViewUserModal(false);
    setSelectedUser(null);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'employer': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'candidate': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const isFormValid = passwordValidation?.isValid &&
    newUser.email &&
    newUser.firstName &&
    newUser.lastName &&
    (newUser.role !== 'employer' || newUser.companyName);

  const hasActiveFilters = searchTerm || selectedRole || statusFilter;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Management</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Manage user accounts, permissions, and activity status
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card-admin p-6">
        <div className="space-y-4">
          {/* Main Filter Row */}
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e as any)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>
            
            {/* Filter Controls */}
            <div className="flex flex-wrap gap-3 items-center">
              {/* Role Filter */}
              <div className="relative">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 pr-10 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-w-[140px] cursor-pointer hover:border-gray-400 dark:hover:border-gray-500"
                >
                  <option value="">All Roles</option>
                  <option value="candidate">Candidates</option>
                  <option value="employer">Employers</option>
                  <option value="admin">Administrators</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Status Filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 pr-10 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-w-[140px] cursor-pointer hover:border-gray-400 dark:hover:border-gray-500"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button 
                  type="button"
                  onClick={(e) => handleSearch(e as any)}
                  className="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <ChevronDownIcon className="w-4 h-4 mr-2" />
                  Search
                </button>

                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="inline-flex items-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Active filters:</span>
              {searchTerm && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => handleClearIndividualFilter('search')}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedRole && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                  Role: {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}
                  <button
                    onClick={() => handleClearIndividualFilter('role')}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-600 focus:outline-none"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                  Status: {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                  <button
                    onClick={() => handleClearIndividualFilter('status')}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-600 focus:outline-none"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Advanced Filters Toggle */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
            >
              <ChevronDownIcon className="w-4 h-4 mr-2" />
              {showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
              <svg 
                className={`w-4 h-4 ml-2 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Advanced Filters
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Additional filter options will be available here in future updates.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Table Header */}
        <div className="bg-gray-50 dark:bg-gray-800 px-8 py-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-xl">
                <UsersIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  User Management
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {pagination.totalItems > 0 && (
                    <>
                      Showing {((pagination.currentPage - 1) * 10) + 1}-{Math.min(pagination.currentPage * 10, pagination.totalItems)} of {pagination.totalItems} users
                      {searchTerm && ` matching "${searchTerm}"`}
                    </>
                  )}
                  {pagination.totalItems === 0 && !loading && (
                    <>No users found{searchTerm && ` matching "${searchTerm}"`}</>
                  )}
                  {loading && 'Loading users...'}
                </p>
                {/* Performance indicator for debugging */}
                {performance.queriedUsers > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Query performance: Retrieved {performance.queriedUsers} users from database
                    {performance.lastLoadTime && ` in ${performance.lastLoadTime}ms`}
                  </p>
                )}
              </div>
            </div>
            
            {/* User Statistics */}
            <div className="flex items-center space-x-6">
              {/* Quick Stats */}
              <div className="hidden md:flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {users.filter(u => u.activeRoles && u.activeRoles.length > 0).length}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600 dark:text-red-400">
                    {users.filter(u => !u.activeRoles || u.activeRoles.length === 0).length}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Inactive</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {users.filter(u => u.emailVerified).length}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Verified</div>
                </div>
              </div>
              
              {/* Add User Button */}
              <button
                onClick={handleOpenAddUserModal}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add User
              </button>
              
              {/* Refresh Button */}
              <button
                onClick={loadUsers}
                disabled={loading}
                className="p-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-xl transition-all duration-200 disabled:opacity-50 group"
              >
                <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-300'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-6 py-4 text-left">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
                    User
                  </span>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
                  Role & Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
                  Verification
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
                  Joined
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-150 group">
                  <td className="px-6 py-6">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 rounded-xl flex items-center justify-center">
                            <span className="text-lg font-bold text-blue-700 dark:text-blue-300">
                              {user.email ? user.email.charAt(0).toUpperCase() : '?'}
                            </span>
                          </div>
                          {user.activeRoles && user.activeRoles.length > 0 && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 animate-pulse"></div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate font-mono">
                          {user.email || 'No email'}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          ID: {user.id}
                        </p>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-6">
                    <div className="space-y-3">
                      {/* Primary Role */}
                      <div>
                        <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-lg shadow-sm ${getRoleBadgeColor(user.role)}`}>
                          <div className="w-2 h-2 bg-current rounded-full mr-2 opacity-70"></div>
                          {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Unknown'} (Primary)
                        </span>
                      </div>
                      
                      {/* Individual Role Controls - Always show for debugging and if user has roles array */}
                      {user.roles && user.roles.length > 0 ? (
                        <div className="space-y-2">
                          <div className="space-y-2">
                            {user.roles.map((roleItem, index) => {
                              console.log('🔍 Rendering role button:', { 
                                userId: user.id, 
                                roleItem: roleItem.role, 
                                isActive: roleItem.isActive,
                                buttonKey: `${user.id}-${roleItem.role}`
                              });
                              
                              return (
                                <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                                  <div className="flex items-center space-x-2">
                                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${getRoleBadgeColor(roleItem.role)}`}>
                                      <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${roleItem.isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                                      {roleItem.role ? roleItem.role.charAt(0).toUpperCase() + roleItem.role.slice(1) : 'Unknown'}
                                    </span>
                                    {roleItem.role === user.role && (
                                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Primary</span>
                                    )}
                                  </div>
                                  
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // console.log('🔘 Button clicked!', { userId: user.id, role: roleItem.role, currentStatus: roleItem.isActive });
                                      handleToggleSpecificRole(user.id, roleItem.role, roleItem.isActive);
                                    }}
                                    disabled={loadingActions[`${user.id}-${roleItem.role}`]}
                                    className={`p-1 rounded-md transition-all duration-200 text-xs font-medium ${
                                      roleItem.isActive
                                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50'
                                        : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/50'
                                    } disabled:opacity-50 cursor-pointer`}
                                    title={roleItem.isActive ? `Deactivate ${roleItem.role} role` : `Activate ${roleItem.role} role`}
                                  >
                                    {loadingActions[`${user.id}-${roleItem.role}`] ? (
                                      <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                                    ) : roleItem.isActive ? (
                                      <XCircleIcon className="w-3 h-3" />
                                    ) : (
                                      <CheckCircleIcon className="w-3 h-3" />
                                    )}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        /* Single Role Display */
                        user.activeRoles && user.activeRoles.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {user.activeRoles.slice(0, 3).map((roleItem, index) => (
                              <span key={index} className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${getRoleBadgeColor(roleItem)}`}>
                                {roleItem ? roleItem.charAt(0).toUpperCase() + roleItem.slice(1) : 'Unknown'}
                              </span>
                            ))}
                            {user.activeRoles.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md">
                                +{user.activeRoles.length - 3}
                              </span>
                            )}
                          </div>
                        )
                      )}
                      
                      {/* Overall Status Indicator */}
                      <div className="flex items-center space-x-2">
                        {user.activeRoles && user.activeRoles.length > 0 ? (
                          <span className="inline-flex items-center text-xs font-medium text-green-700 dark:text-green-400">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></div>
                            {user.roles && user.roles.length > 1 ? 
                              `${user.activeRoles.length}/${user.roles.length} roles active` : 
                              'Active'
                            }
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs font-medium text-red-700 dark:text-red-400">
                            <div className="w-2 h-2 bg-red-500 rounded-full mr-1.5"></div>
                            All roles inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-6">
                    <div className="space-y-2">
                      {user.emailVerified ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                            <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-green-700 dark:text-green-400">
                              Verified
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Email confirmed
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/50 rounded-lg flex items-center justify-center">
                            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                              Pending
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Not verified
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-6">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatDate(user.createdAt)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {(() => {
                        const days = Math.floor((new Date().getTime() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24));
                        if (days === 0) return 'Today';
                        if (days === 1) return 'Yesterday';
                        if (days < 30) return `${days} days ago`;
                        if (days < 365) return `${Math.floor(days / 30)} months ago`;
                        return `${Math.floor(days / 365)} years ago`;
                      })()}
                    </div>
                  </td>
                  
                  <td className="px-6 py-6">
                    <div className="flex items-center space-x-2">
                      {/* View Button */}
                      <button
                        onClick={() => handleOpenViewUserModal(user)}
                        className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-lg transition-all duration-200 group/btn"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4 group-hover/btn:scale-110 transition-transform duration-200" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enhanced Empty State */}
        {users.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6">
              <UsersIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {searchTerm ? 'No users found' : 'No users yet'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {searchTerm 
                ? `No users match your search for "${searchTerm}". Try adjusting your search terms.`
                : 'Users will appear here once they register for the platform.'
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {/* Enhanced Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              {/* Pagination Info */}
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                </p>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {pagination.totalItems} total users
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                {/* First Page */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: 1 }))}
                  disabled={pagination.currentPage === 1 || loading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="First page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>

                {/* Previous Page */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  disabled={!pagination.hasPrev || loading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous page"
                >
                  <ChevronDownIcon className="w-4 h-4 rotate-90" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const pages = [];
                    const currentPage = pagination.currentPage;
                    const totalPages = pagination.totalPages;
                    
                    // Show page numbers with ellipsis
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, currentPage + 2);
                    
                    // Adjust range if we're near the beginning or end
                    if (currentPage <= 3) {
                      endPage = Math.min(totalPages, 5);
                    }
                    if (currentPage >= totalPages - 2) {
                      startPage = Math.max(1, totalPages - 4);
                    }
                    
                    // Add first page and ellipsis if needed
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: 1 }))}
                          className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="start-ellipsis" className="px-2 text-gray-400">...</span>);
                      }
                    }
                    
                    // Add page numbers
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: i }))}
                          disabled={loading}
                          className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 disabled:opacity-50 ${
                            i === currentPage
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // Add last page and ellipsis if needed
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="end-ellipsis" className="px-2 text-gray-400">...</span>);
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: totalPages }))}
                          className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>

                {/* Next Page */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  disabled={!pagination.hasNext || loading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next page"
                >
                  <ChevronDownIcon className="w-4 h-4 -rotate-90" />
                </button>

                {/* Last Page */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: pagination.totalPages }))}
                  disabled={pagination.currentPage === pagination.totalPages || loading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Last page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm flex items-center justify-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-blue-300 rounded-full animate-ping"></div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                Loading users...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full max-h-[95vh] overflow-hidden animate-in zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-xl">
                    <PlusIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Add New User
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Create a new user account
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseAddUserModal}
                  className="p-2 hover:bg-white/70 dark:hover:bg-gray-600/70 rounded-xl transition-all duration-200 group"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleAddUser} className="overflow-y-auto max-h-[calc(95vh-180px)]">
              <div className="px-6 py-6 space-y-6 bg-gray-50/30 dark:bg-gray-800/30">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Enter email address"
                  />
                </div>

                {/* Password */}
                <PasswordInput
                  value={newUser.password}
                  onChange={handlePasswordChange}
                  label="Password"
                  required
                  onValidationChange={handlePasswordValidation}
                  placeholder="Enter your password"
                  showValidation={true}
                />

                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Enter first name"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Enter last name"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => {
                      const newRole = e.target.value as 'candidate' | 'employer' | 'admin';
                      setNewUser({ 
                        ...newUser, 
                        role: newRole,
                        // Clear company name if switching away from employer
                        companyName: newRole === 'employer' ? newUser.companyName : ''
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  >
                    <option value="candidate">Candidate</option>
                    <option value="employer">Employer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Company Name - Only show for employers */}
                {newUser.role === 'employer' && (
                  <div className="animate-in slide-in-from-top duration-300">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newUser.companyName}
                      onChange={(e) => setNewUser({ ...newUser, companyName: e.target.value })}
                      required={newUser.role === 'employer'}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter company name"
                    />
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseAddUserModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addUserLoading || !isFormValid}
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                  >
                    {addUserLoading && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    <PlusIcon className="w-4 h-4" />
                    Add User
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View User Modal - Modernized */}
      {showViewUserModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-6xl max-h-[95vh] overflow-hidden animate-in zoom-in-95 duration-300">
            
            {/* Enhanced Modal Header */}
            <div className="relative bg-gray-50 dark:bg-gray-800 px-8 py-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-xl">
                    <UserCircleIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedUser.email}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      User Profile & Activity Overview
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={handleCloseViewUserModal}
                  className="p-2 hover:bg-white/70 dark:hover:bg-gray-700/70 rounded-xl transition-all duration-200 group"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
                </button>
              </div>
            </div>

            {/* Enhanced Modal Body */}
            <div className="p-8 overflow-y-auto max-h-[calc(95vh-200px)] bg-gray-50/30 dark:bg-gray-800/30">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                
                {/* User Information Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      User Information
                    </h4>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Contact Information
                        </div>
                        <div className="space-y-3">
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</span>
                            <div className="text-base text-gray-900 dark:text-gray-100 font-mono bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 mt-1">
                              {selectedUser.email}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">User ID</span>
                            <div className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 mt-1">
                              {selectedUser.id}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Role Information */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                        <div className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-3">
                          Role & Permissions
                        </div>
                        <div className="space-y-3">
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Primary Role</span>
                            <div className="mt-2">
                              <span className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl shadow-sm ${getRoleBadgeColor(selectedUser.role)}`}>
                                <div className="w-2 h-2 bg-current rounded-full mr-2 opacity-70"></div>
                                {selectedUser.role ? selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1) : 'Unknown'}
                              </span>
                            </div>
                          </div>
                          
                          {selectedUser.activeRoles && selectedUser.activeRoles.length > 1 && (
                            <div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">All Active Roles</span>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {selectedUser.activeRoles.map((roleItem, index) => (
                                  <span key={index} className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-lg ${getRoleBadgeColor(roleItem)}`}>
                                    <div className="w-1.5 h-1.5 bg-current rounded-full mr-1.5 opacity-70"></div>
                                    {roleItem ? roleItem.charAt(0).toUpperCase() + roleItem.slice(1) : 'Unknown'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-gray-700/50 rounded-xl p-4 border-l-4 border-blue-500">
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Account Status
                          </div>
                          <div className="mt-2">
                            {selectedUser.activeRoles && selectedUser.activeRoles.length > 0 ? (
                              <span className="inline-flex items-center text-sm font-semibold text-green-700 dark:text-green-400">
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-sm font-semibold text-red-700 dark:text-red-400">
                                <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                Inactive
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-700/50 rounded-xl p-4 border-l-4 border-yellow-500">
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Email Status
                          </div>
                          <div className="mt-2">
                            {selectedUser.emailVerified ? (
                              <span className="inline-flex items-center text-sm font-semibold text-green-700 dark:text-green-400">
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                Verified
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-sm font-semibold text-yellow-700 dark:text-yellow-400">
                                <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2 animate-pulse"></div>
                                Pending
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Join Date */}
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                        <div className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wide mb-2">
                          Member Since
                        </div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {formatDate(selectedUser.createdAt)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {(() => {
                            const days = Math.floor((new Date().getTime() - new Date(selectedUser.createdAt).getTime()) / (1000 * 60 * 60 * 24));
                            return `${days} days ago`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity History Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                      Activity Timeline
                    </h4>
                  </div>
                  
                  <div className="p-6">
                    {loadingHistory ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="relative">
                          <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
                          <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-blue-300 rounded-full animate-ping"></div>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">Loading activity history...</p>
                      </div>
                    ) : userActionHistory.length > 0 ? (
                      <div className="space-y-4 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                        {userActionHistory.slice(0, 20).map((action, index) => (
                          <div key={index} className="relative pl-8 pb-4 group">
                            {/* Timeline line */}
                            <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 to-gray-200 dark:from-blue-700 dark:to-gray-600"></div>
                            
                            {/* Timeline dot */}
                            <div className="absolute left-1.5 top-2 w-3 h-3 bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-full shadow-sm group-hover:scale-125 transition-transform duration-200"></div>
                            
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-2xl">{action.icon}</span>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded-lg">
                                    {new Date(action.timestamp).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {action.type === 'database' ? (
                                    // Access the nested data object for enhanced fields
                                    action.data?.description || 
                                    action.data?.summary || 
                                    action.description || 
                                    action.summary || 
                                    (action.data?.action && action.data?.role ? `${action.data.role ? action.data.role.charAt(0).toUpperCase() + action.data.role.slice(1) : 'Unknown'} role ${action.data.newStatus ? 'activated' : 'deactivated'}` : null) ||
                                    (action.data?.action === 'toggle_user_status' ? `User ${action.data.newStatus ? 'activated' : 'deactivated'} (all roles)` : null) ||
                                    (action.data?.action === 'toggle_role_status' ? `Role status changed` : null) ||
                                    (action.action && action.role ? `${action.role ? action.role.charAt(0).toUpperCase() + action.role.slice(1) : 'Unknown'} role ${action.newStatus ? 'activated' : 'deactivated'}` : null) ||
                                    (action.action === 'toggle_user_status' ? `User ${action.newStatus ? 'activated' : 'deactivated'} (all roles)` : null) ||
                                    (action.action === 'toggle_role_status' ? `Role status changed` : null) ||
                                    `${action.operation?.toUpperCase() || 'UPDATE'} in ${action.collection || 'users'}`
                                  ) : action.type === 'auth' ? (
                                    `${action.action?.toUpperCase() || 'LOGIN'} ${action.success ? '✓' : '✗'}`
                                  ) : (
                                    'User Action'
                                  )}
                                </div>
                                
                                {/* Enhanced activity details */}
                                <div className="text-xs text-gray-600 dark:text-gray-300 space-y-2">
                                  {action.type === 'database' && action.performedByEmail && (
                                    <div className="flex items-center space-x-2">
                                      <span className="text-gray-500">Performed by:</span>
                                      <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-md font-medium">
                                        {action.performedByEmail}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {action.type === 'database' && (action.data?.action || action.action || action.changes) && (
                                    <div className="grid grid-cols-1 gap-2">
                                      {(action.data?.action || action.action) && (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-gray-500 text-xs">Action:</span>
                                          <span className="font-mono text-xs bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                                            {action.data?.action || action.action}
                                          </span>
                                        </div>
                                      )}
                                      
                                      {(action.data?.role || action.role) && (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-gray-500 text-xs">Role:</span>
                                          <span className="font-mono text-xs bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded text-blue-800 dark:text-blue-200">
                                            {action.data?.role || action.role}
                                          </span>
                                        </div>
                                      )}
                                      
                                      {((action.data?.previousStatus !== undefined && action.data?.newStatus !== undefined) || 
                                        (action.previousStatus !== undefined && action.newStatus !== undefined)) && (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-gray-500 text-xs">Status:</span>
                                          <div className="flex items-center space-x-1">
                                            <span className={`font-mono text-xs px-2 py-1 rounded ${(action.data?.previousStatus || action.previousStatus) ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                                              {(action.data?.previousStatus || action.previousStatus) ? 'Active' : 'Inactive'}
                                            </span>
                                            <span className="text-gray-400">→</span>
                                            <span className={`font-mono text-xs px-2 py-1 rounded ${(action.data?.newStatus || action.newStatus) ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'}`}>
                                              {(action.data?.newStatus || action.newStatus) ? 'Active' : 'Inactive'}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {(action.data?.affectedRoles || action.affectedRoles) && (action.data?.affectedRoles || action.affectedRoles).length > 0 && (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-gray-500 text-xs">Affected roles:</span>
                                          <span className="font-mono text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded">
                                            {(action.data?.affectedRoles || action.affectedRoles).join(', ')}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {action.type === 'auth' && (
                                    <div className="space-y-1">
                                      {action.errorMessage && (
                                        <div className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded text-xs">
                                          {action.errorMessage}
                                        </div>
                                      )}
                                      {action.ip && (
                                        <div className="text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                                          IP: {action.ip}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {userActionHistory.length > 20 && (
                          <div className="text-center py-4 border-t border-gray-200 dark:border-gray-600">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Showing latest 20 of {userActionHistory.length} activities
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                          <UserCircleIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No activity history found for this user
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Activity will appear here once the user starts interacting with the system
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-800/50 px-8 py-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Last updated: {new Date().toLocaleString()}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCloseViewUserModal}
                   
                    className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;